import { useEffect, useState } from 'react';
import { Alert, Badge, Modal, Spinner } from 'react-bootstrap';
import { adminGetUser, imageUrl } from '../api';
import UserRolesEditor from './UserRolesEditor';

// user_management_and_delivery_kyc.md - the website owner opens the COMPLETE details of a user
// account in a popup: every role, status, address, and (for a delivery person) the full Aadhaar
// number + a link to the uploaded card. Fetches the single-user endpoint, which returns the
// un-masked Aadhaar number (the list view masks it).
const ROLE_LABEL = {
  customer: 'Customer', owner: 'Rental owner', admin: 'Website owner', delivery: 'Delivery',
  finance_employee: 'Finance (view)', finance_lead: 'Finance lead', finance_manager: 'Finance manager'
};

function line(addr) {
  if (!addr) return null;
  return [addr.line1, addr.line2, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || null;
}

export default function UserDetailsModal({ userId, show, onHide, onChanged }) {
  const [u, setU] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !userId) return;
    setU(null); setError(''); setLoading(true);
    adminGetUser(userId)
      .then(setU)
      .catch(() => setError('Could not load this user.'))
      .finally(() => setLoading(false));
  }, [show, userId]);

  const roles = u?.effectiveRoles || (u?.role ? [u.role] : []);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{u ? u.name : 'User details'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" size="sm" role="status" />}
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {u && (
          <>
            <div className="d-flex flex-wrap gap-1 mb-2">
              {roles.map((r) => <Badge key={r} bg="secondary">{ROLE_LABEL[r] || r}</Badge>)}
              <Badge bg={u.status === 'disabled' ? 'danger' : 'success'}>{u.status || 'active'}</Badge>
            </div>
            <div className="small mb-1">{u.email}</div>

            <h6 className="text-uppercase text-muted small mt-3">Roles</h6>
            <UserRolesEditor user={u} onChanged={(updated) => { setU(updated); onChanged?.(); }} />


            {u.phone && <div className="small mb-1">📞 {u.phone}</div>}
            {u.createdAt && <div className="small text-muted mb-2">Joined {new Date(u.createdAt).toLocaleDateString()}</div>}

            {line(u.address) && (
              <>
                <h6 className="text-uppercase text-muted small mt-3">Address</h6>
                <div className="small">{line(u.address)}</div>
              </>
            )}
            {u.serviceArea && (
              <>
                <h6 className="text-uppercase text-muted small mt-3">Service area</h6>
                <div className="small">{u.serviceArea}</div>
              </>
            )}
            {u.kyc?.aadhaarNumber && (
              <>
                <h6 className="text-uppercase text-muted small mt-3">Aadhaar KYC</h6>
                <div className="small font-monospace">{u.kyc.aadhaarNumber}</div>
                {u.kyc.aadhaarCardUrl && (
                  <div className="small">
                    <a href={imageUrl(u.kyc.aadhaarCardUrl)} target="_blank" rel="noreferrer">View uploaded Aadhaar card</a>
                  </div>
                )}
                {/* staff_selfie_kyc.md - camera selfie of the person. */}
                {u.kyc.selfieUrl && (
                  <div className="small mt-1 d-flex align-items-center gap-2">
                    <img src={imageUrl(u.kyc.selfieUrl)} alt="Selfie" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                    <a href={imageUrl(u.kyc.selfieUrl)} target="_blank" rel="noreferrer">View selfie</a>
                  </div>
                )}
                {/* v5_fix_issues #1 - driving-licence photo. */}
                {(u.kyc.drivingLicenceNumber || u.kyc.drivingLicenceUrl) && (
                  <div className="small mt-1">
                    {u.kyc.drivingLicenceNumber && <span className="font-monospace me-2">DL: {u.kyc.drivingLicenceNumber}</span>}
                    {u.kyc.drivingLicenceUrl && (
                      <a href={imageUrl(u.kyc.drivingLicenceUrl)} target="_blank" rel="noreferrer">View driving licence</a>
                    )}
                  </div>
                )}
                <div className="small">
                  {u.kyc.verified
                    ? <Badge bg="success">Verified</Badge>
                    : <Badge bg="warning" text="dark">Not verified</Badge>}
                </div>
              </>
            )}
            {u.savedAddress && line(u.savedAddress) && (
              <div className="small text-muted mt-3">Last delivery address on file: {line(u.savedAddress)}</div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
